import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

/**
 * Export mapped payments in the layout of Tally's
 * "Vouchers-V12 Receipts and Payments with multiple references" sheet.
 *
 * Everything here — headers, the marker row, the silver spacer, the row the data
 * starts on, column widths, fonts, fills, row heights and number formats — is
 * taken from that template so the file can be handed straight to Tally's import
 * mapping without touching it.
 */

const num = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

// Row 1, verbatim from the template. "AMOUNT" legitimately appears twice
// (party amount in H, bank amount in M), which is why rows are built as a grid
// rather than as objects.
export const TALLY_HEADERS = [
  'UNIQUEID', 'BASE VCH-TYPE', 'VOUCHER TYPE', 'VCH.NO', 'DATE', 'PARTY NAME', 'PARENT GROUP', 'AMOUNT',
  'REFERENCE NO', 'REFERENCE AMT', 'REFERENCE TYPE', 'CASH or BANK LEDGER', 'AMOUNT',
  'Ledger1Name', 'Ledger1Amt', 'Ledger2Name', 'Ledger2Amt', 'Ledger3Name', 'Ledger3Amt',
  'Ledger4Name', 'Ledger4Amt', 'Ledger5Name', 'Ledger5Amt', 'Ledger6Name', 'Ledger6Amt',
  'Ledger7Name', 'Ledger7Amt', 'Ledger8Name', 'Ledger8Amt', 'Ledger9Name', 'Ledger9Amt',
  'Ledger10Name', 'Ledger10Amt',
  'NARRATION', 'INSTRUMENT NO', 'INSTRUMENT DATE', 'TRANSACTION-TYPE', 'FAVOURING',
  'CHEQUECROSSCOMMENT', 'BANKDATE',
];

const TALLY_MAP_MARKER = '** MAP YOUR EXCEL COLUMNS FROM HERE***';

// udiMagic reads its mapping from a comment on the UNIQUEID cell. Without it the
// import has no idea which XML tag set to apply, so it travels with every export.
const TALLY_A1_NOTE =
  '<XMLTAGSFILENAME>Vouchers-V12-Receipts-and-Payments-with-multiple-references-xml-tags.xml</XMLTAGSFILENAME>' +
  '<VERSION>12</VERSION><RELEASE>2.5</RELEASE>';

const FONT = { name: 'Tahoma', size: 10 };
const HEADER_FILL = 'FFFF0000'; // row 1 — red
const MARKER_FILL = 'FF00FF00'; // row 2 — green
const SPACER_FILL = 'FFC0C0C0'; // row 3 — silver
const HEADER_ROW_HEIGHT = 24.95;
const MARKER_ROW_HEIGHT = 30;

// ── template layout, read off the original file ──

// column widths in characters, one per header
const COL_WIDTHS = [
  10, 15.14, 16, 8.43, 11.57, 17.14, 15, 8.57, 16, 17.43, 17.43, 22, 9.71, 13.43, 12.57, 12.86,
  11.86, 11.86, 11.86, 11.86, 11.86, 12.86, 11.86, 11.86, 11.86, 11.86, 11.86, 11.86, 11.86, 11.86,
  11.86, 11.86, 11.86, 11.86, 15.14, 19.14, 19.14, 15.71, 24.29, 10.29,
];

const DATE_FMT = 'yyyy/mm/dd';
const AMOUNT_FMT = '#,##0';
const DATE_COLS = [5, 36, 40]; // DATE, INSTRUMENT DATE, BANKDATE (1-based)
const AMOUNT_COLS = [8, 10, 13, 15, 17, 19, 21, 23, 25, 27, 29, 31, 33];

// Header row 1, marker row 2, silver spacer row 3 — data therefore starts on row 4.
const FIRST_DATA_ROW = 4;

// 0-based positions inside a data row, named so the builder reads clearly
const T = {
  uniqueId: 0, baseVchType: 1, vchType: 2, vchNo: 3, date: 4, party: 5, parentGroup: 6, partyAmt: 7,
  refNo: 8, refAmt: 9, refType: 10, bankLedger: 11, bankAmt: 12,
  led1Name: 13, led1Amt: 14, narration: 33, instrumentNo: 34, instrumentDate: 35,
  txnType: 36, favouring: 37, crossComment: 38, bankDate: 39,
};

// Ledger names exactly as they exist in Tally. A mismatch here makes the import
// create a new ledger instead of posting to the right one.
const AXIS_BANK_LEDGER = 'Axis Bank';
const ICICI_BANK_LEDGER = 'ICICI Bank (Transaction account)';
const TDS_LEDGER = 'TDS Receivable FY 2026-2027';

// Statement uploads build the id as AXIS + the bank reference, so the prefix is
// what tells us the credit came through Axis. bank_type is honoured too when the
// API happens to send it; anything else is the ICICI account.
const bankLedgerOf = (voucher) => {
  const id = (voucher?.trx_id ?? '').toString().trim().toUpperCase();
  const type = (voucher?.bank_type ?? '').toString().trim().toUpperCase();
  return id.startsWith('AXIS') || type === 'AXIS' ? AXIS_BANK_LEDGER : ICICI_BANK_LEDGER;
};

// Tally's own transaction types for the banking allocation
const txnTypeOf = (mode) => {
  const m = (mode ?? '').toString().toUpperCase().trim();
  if (['CHQ', 'CHEQUE', 'CLG'].includes(m)) return 'Cheque';
  if (['NEFT', 'RTGS', 'IMPS', 'UPI', 'IFT', 'ACH', 'ECS', 'TRF', 'TRFR'].includes(m)) return 'e-Fund Transfer';
  if (m === 'CASH') return 'Cash';
  return m ? 'Others' : '';
};

// A pure date, no time component. Excel dates are written from UTC, so building
// the value at UTC midnight from the *local* calendar parts keeps the day the
// screen shows and leaves the time at exactly 00:00 — a plain timestamp would
// otherwise carry the timezone offset and could land on the previous day.
const dateValue = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
};

const round2 = (n) => Math.round((num(n) + Number.EPSILON) * 100) / 100;

// UNIQUEID rules for the import tool: never blank, max 24 characters, unique per
// voucher, and the column sorted with every row of a voucher adjacent. A plain
// running number satisfies all of them, so the id is generated here rather than
// derived from the transaction id (which can exceed 24 characters). The
// transaction id still travels in the narration.
const UNIQUE_ID_PREFIX = 'A';
const UNIQUE_ID_PAD = 4;
const sequentialId = (n) => `${UNIQUE_ID_PREFIX}${String(n).padStart(UNIQUE_ID_PAD, '0')}`;
const MAX_UNIQUE_ID = 24;

// The client's own code is the voucher id, used as-is — a client paying twice in
// one file carries the same id on both vouchers. Only a missing code falls back to
// a number, because a blank id imports nothing at all.
const clientVoucherId = (code, fallbackIndex) => {
  // kept exactly as the client code reads — case included — with only spaces and
  // punctuation dropped, since the id has to be alphanumeric
  const base = (code ?? '').toString().trim().replace(/[^A-Za-z0-9-]/g, '');
  return base ? base.slice(0, MAX_UNIQUE_ID) : sequentialId(fallbackIndex);
};

/**
 * Bank credits → vouchers, shared by BOTH exports so the xlsx and the XML always
 * describe the same receipts. A line with no trx_id belongs to no voucher and is
 * skipped; clients are keyed by their Tally ledger, so two of our clients sharing
 * a ledger become one party.
 */
const groupVouchers = (lines) => {
  // A line whose client has no Tally ledger cannot be posted: Tally would create
  // a ledger rather than reject the voucher. The whole transaction is dropped,
  // not just that line, because a partial voucher would book less than the bank
  // actually received. The screen filters these out too; this is the backstop.
  const unpostable = new Set();
  (lines || []).forEach((l) => {
    if (l.trx_id && !String(l.tally_ledger_name ?? '').trim()) unpostable.add(String(l.trx_id));
  });

  const byTrx = new Map();
  (lines || []).forEach((l) => {
    const key = String(l.trx_id ?? '');
    if (!key || unpostable.has(key)) return;
    if (!byTrx.has(key)) {
      byTrx.set(key, {
        trx_id: key,
        date: l.payment_date,
        mode: l.trx_mode,
        narration: l.trx_description || l.description || '', // the bank narration of this trx_id
        bank_type: l.bank_type,
        clients: new Map(),
      });
    }
    const v = byTrx.get(key);
    const ledger = String(l.tally_ledger_name ?? '').trim(); // guaranteed non-empty by the filter above
    const ck = ledger.toUpperCase();
    if (!v.clients.has(ck)) {
      v.clients.set(ck, {
        name: ledger,
        code: (l.unique_id || l.client_id || l.client_fk || '').toString().trim(),
        invoices: [],
      });
    }
    v.clients.get(ck).invoices.push(l);
  });
  // oldest payment first
  return Array.from(byTrx.values()).sort(
    (a, b) =>
      new Date(a.date || 0).getTime() - new Date(b.date || 0).getTime() ||
      String(a.trx_id).localeCompare(String(b.trx_id), undefined, { numeric: true })
  );
};

/**
 * Mapped invoice lines → voucher rows, one row per invoice reference.
 * Within a voucher only the FIRST row carries the date, voucher type and bank
 * ledger; further references repeat just UNIQUEID + the reference columns, and a
 * second party inside the same voucher repeats its name and amount only.
 */
export const buildTallyVoucherRows = (lines) => {
  // The id is the paying client's own code; every row of a voucher repeats it,
  // so the rows of one voucher stay together.
  const vouchers = groupVouchers(lines).map((v, i) => {
    const firstClient = Array.from(v.clients.values())[0];
    return { v, uniqueId: clientVoucherId(firstClient?.code, i + 1) };
  });

  const rows = [];
  vouchers.forEach(({ v, uniqueId }) => {
    const clients = Array.from(v.clients.values());
    const allLines = clients.flatMap((c) => c.invoices);
    // the bank receives the payment portion; TDS is credited through its own ledger
    const bankAmt = round2(allLines.reduce((s, i) => s + num(i.allocated), 0));
    const tdsAmt = round2(allLines.reduce((s, i) => s + num(i.tds_amount), 0));
    let firstOfVoucher = true;

    clients.forEach((c) => {
      const partyAmt = round2(c.invoices.reduce((s, i) => s + num(i.allocated) + num(i.tds_amount), 0));
      c.invoices.forEach((inv, idx) => {
        const row = new Array(TALLY_HEADERS.length).fill('');
        row[T.uniqueId] = uniqueId;
        if (idx === 0) {
          row[T.party] = c.name;
          row[T.partyAmt] = partyAmt;
        }
        if (firstOfVoucher) {
          row[T.baseVchType] = 'Receipt';
          row[T.vchType] = 'Receipt';
          row[T.date] = dateValue(v.date);
          row[T.bankLedger] = bankLedgerOf(v);
          row[T.bankAmt] = bankAmt;
          if (tdsAmt > 0) {
            row[T.led1Name] = TDS_LEDGER;
            row[T.led1Amt] = tdsAmt;
          }
          row[T.narration] = v.narration || v.trx_id; // fall back to the id when there is no narration
          row[T.txnType] = txnTypeOf(v.mode);
          firstOfVoucher = false;
        }
        // an invoice is knocked off by the payment plus any TDS credited against it
        row[T.refNo] = inv.reference_no ?? '';
        row[T.refAmt] = round2(num(inv.allocated) + num(inv.tds_amount));
        row[T.refType] = 'Agst Ref';
        rows.push(row);
      });
    });
  });
  return rows;
};

const solidFill = (argb) => ({ type: 'pattern', pattern: 'solid', fgColor: { argb } });

// paint every column of a row, not just the ones holding text
const paintRow = (row, argb) => {
  for (let c = 1; c <= TALLY_HEADERS.length; c += 1) {
    const cell = row.getCell(c);
    cell.fill = solidFill(argb);
    cell.font = FONT;
  }
};

/** Write the workbook and hand it to the browser. */
export const exportTallyVouchers = async (dataRows, filename) => {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Sheet1');
  ws.columns = COL_WIDTHS.map((width) => ({ width }));

  // row 1 — headers on red
  const header = ws.addRow(TALLY_HEADERS);
  header.height = HEADER_ROW_HEIGHT;
  paintRow(header, HEADER_FILL);
  header.eachCell({ includeEmpty: true }, (cell) => {
    cell.font = { ...FONT, bold: true };
    cell.alignment = { vertical: 'middle', wrapText: true };
  });

  // the mapping pointer udiMagic looks for, on the UNIQUEID cell
  header.getCell(1).note = {
    texts: [
      {
        font: { name: 'Tahoma', family: 2, size: 9, bold: true, color: { indexed: 81 } },
        text: TALLY_A1_NOTE,
      },
    ],
    margins: { insetmode: 'auto' },
    protection: { locked: 'True', lockText: 'False' },
    editAs: 'twoCells',
  };

  // row 2 — the mapping marker on green
  const marker = ws.addRow([TALLY_MAP_MARKER]);
  marker.height = MARKER_ROW_HEIGHT;
  paintRow(marker, MARKER_FILL);
  marker.getCell(1).value = TALLY_MAP_MARKER;
  marker.getCell(1).font = { ...FONT, bold: true };

  // row 3 — the silver spacer the template keeps above its data
  paintRow(ws.addRow([]), SPACER_FILL);

  // row 4 onwards — the vouchers
  dataRows.forEach((r) => {
    const row = ws.addRow(r);
    row.eachCell({ includeEmpty: false }, (cell) => {
      cell.font = FONT;
    });
    DATE_COLS.forEach((c) => {
      row.getCell(c).numFmt = DATE_FMT;
    });
    AMOUNT_COLS.forEach((c) => {
      row.getCell(c).numFmt = AMOUNT_FMT;
    });
  });

  const buf = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename);
  return { rows: dataRows.length, firstDataRow: FIRST_DATA_ROW };
};

/* ─────────── suspense sheet (Template.xls) ───────────
 * A bank-statement shaped import: one row per parked credit, posted between the
 * bank ledger and the suspense account. Headers, their fill colours, the two
 * blank rows under them and the column widths are taken from that template.
 */
const SUSPENSE_HEADERS = [
  'DATE', 'NARRATION', 'CH.NO', 'WITHDRAWAL', 'DEPOSIT', 'Bank Ledger Name',
  'Other Ledger Name ', 'Group for Other Ledger', 'VoucherType', 'Instrument Date',
  'Transaction-type', 'Favouring', 'ChequeCrossComment', 'Bank Date', 'Bank Remarks',
];

// the template colour-codes the header by purpose: bank columns red, narration
// yellow, ledger columns green, the optional tail pale yellow
const SUSPENSE_HEADER_FILLS = [
  'FFFF0000', 'FFFFFF00', 'FFFFFF00', 'FFFF0000', 'FFFF0000', 'FFFF0000',
  'FFCCFFCC', 'FFCCFFCC', 'FFCCFFCC', 'FFFFFFCC', 'FFFFFFCC', 'FFFFFFCC',
  'FFFFFFCC', 'FFFFFFCC', 'FFFFFFCC',
];

const SUSPENSE_COL_WIDTHS = [
  15.17, 108.33, 10.5, 15.17, 14.33, 21.67, 24, 24, 15.33, 14.67, 16.83, 14.83, 21.83, 10.33, 13.5,
];

const SUSPENSE_LEDGER = 'Suspense A/c';
// Payments booked as something other than an invoice receipt park in their own
// account, so Tally can tell the two apart on import.
const OTHER_SUSPENSE_LEDGER = 'Other Suspense A/c';
const AMOUNT_FMT_2DP = '#,##0.00';
const SUSPENSE_AMOUNT_COLS = [4, 5]; // WITHDRAWAL, DEPOSIT (1-based)
// header on row 1, two blank rows, data from row 4 — exactly as the template
const SUSPENSE_BLANK_ROWS = 2;

// the template holds dates as dd/mm/yyyy text, not as date values
const ddmmyyyy = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const p2 = (n) => String(n).padStart(2, '0');
  return `${p2(d.getDate())}/${p2(d.getMonth() + 1)}/${d.getFullYear()}`;
};

/**
 * Parked and non-invoice payments → template rows. Every credit is a deposit.
 * `is_other` picks the ledger: a payment booked as something other than an
 * invoice receipt posts to "Other Suspense A/c", a parked one to "Suspense A/c".
 */
export const buildSuspenseRows = (payments) =>
  (payments || []).map((p) => {
    const row = new Array(SUSPENSE_HEADERS.length).fill('');
    row[0] = ddmmyyyy(p.payment_date);
    row[1] = (p.description ?? '').toString();
    row[2] = (p.cheque_no ?? p.instrument_no ?? '').toString(); // CH.NO
    row[3] = ''; // WITHDRAWAL — these are always money in
    row[4] = round2(p.amount);
    row[5] = bankLedgerOf(p);
    row[6] = p.is_other ? OTHER_SUSPENSE_LEDGER : SUSPENSE_LEDGER;
    row[14] = (p.parked_reason ?? '').toString(); // Bank Remarks
    return row;
  });

/** Write the suspense sheet and hand it to the browser. */
export const exportSuspenseSheet = async (dataRows, filename) => {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Sheet1');
  ws.columns = SUSPENSE_COL_WIDTHS.map((width) => ({ width }));

  const header = ws.addRow(SUSPENSE_HEADERS);
  header.eachCell({ includeEmpty: true }, (cell, col) => {
    cell.fill = solidFill(SUSPENSE_HEADER_FILLS[col - 1] || 'FFFFFFFF');
    cell.font = { bold: true };
  });

  for (let i = 0; i < SUSPENSE_BLANK_ROWS; i += 1) ws.addRow([]);

  dataRows.forEach((r) => {
    const row = ws.addRow(r);
    SUSPENSE_AMOUNT_COLS.forEach((c) => {
      row.getCell(c).numFmt = AMOUNT_FMT_2DP;
    });
  });

  const buf = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), filename);
  return { rows: dataRows.length };
};

/* ─────────── Tally XML (import format) ───────────
 * The same vouchers as the xlsx export, written as an "Import Data" envelope so
 * the file can be imported straight into Tally (Gateway → Import Data → Vouchers).
 *
 * This is deliberately NOT the <DSPVCH…> shape Tally produces when it exports a
 * day book: those are display fields for a report and Tally's importer ignores
 * them. The structure below — IMPORTDATA / REQUESTDATA / TALLYMESSAGE / VOUCHER —
 * is what the importer actually reads.
 *
 * Sign convention, which is the thing most easily got wrong: inside
 * ALLLEDGERENTRIES.LIST a debit is a NEGATIVE amount and a credit is POSITIVE.
 * So for a receipt the bank and TDS ledgers carry negative amounts and the party
 * carries a positive one, and the entries sum to zero.
 */

const XML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' };
const xmlEscape = (v) => (v ?? '').toString().replace(/[&<>"']/g, (c) => XML_ESCAPES[c]);

// Tally reads dates as YYYYMMDD. Built from the local calendar parts so the day
// on screen is the day imported, whatever the timezone.
const tallyImportDate = (v) => {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const p2 = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${p2(d.getMonth() + 1)}${p2(d.getDate())}`;
};

const amt2 = (n) => round2(n).toFixed(2);

// A ledger entry. `debit` flips both the sign and ISDEEMEDPOSITIVE, which Tally
// expects to agree with each other.
const ledgerEntry = (name, amount, debit, bills) => {
  const out = [
    '      <ALLLEDGERENTRIES.LIST>',
    `       <LEDGERNAME>${xmlEscape(name)}</LEDGERNAME>`,
    `       <ISDEEMEDPOSITIVE>${debit ? 'Yes' : 'No'}</ISDEEMEDPOSITIVE>`,
    `       <AMOUNT>${debit ? '-' : ''}${amt2(amount)}</AMOUNT>`,
  ];
  // Bill references knock the payment off the named invoices. "Agst Ref" matches
  // an invoice that already exists in Tally; a reference it cannot find would be
  // created as a new bill instead, so the numbers have to be the Tally ones.
  (bills || []).forEach((bill) => {
    out.push('       <BILLALLOCATIONS.LIST>');
    // An unreferenced amount still has to be allocated, or Tally rejects the
    // voucher as a bill-wise mismatch — it posts on account instead, exactly as
    // the xlsx export leaves such a line with a blank reference number.
    if (bill.name) {
      out.push(`        <NAME>${xmlEscape(bill.name)}</NAME>`);
      out.push('        <BILLTYPE>Agst Ref</BILLTYPE>');
    } else {
      out.push('        <BILLTYPE>On Account</BILLTYPE>');
    }
    out.push(`        <AMOUNT>${debit ? '-' : ''}${amt2(bill.amount)}</AMOUNT>`);
    out.push('       </BILLALLOCATIONS.LIST>');
  });
  out.push('      </ALLLEDGERENTRIES.LIST>');
  return out;
};

/**
 * Mapped invoice lines → a Tally "Import Data" document.
 *
 * One <VOUCHER> per bank credit. The bank ledger is debited with the money
 * received, the TDS ledger with anything withheld, and each paying client is
 * credited with its share and carries a bill reference per invoice settled.
 */
export const buildTallyXml = (lines) => {
  const vouchers = groupVouchers(lines);

  const out = [
    '<ENVELOPE>',
    ' <HEADER>',
    '  <TALLYREQUEST>Import Data</TALLYREQUEST>',
    ' </HEADER>',
    ' <BODY>',
    '  <IMPORTDATA>',
    '   <REQUESTDESC>',
    '    <REPORTNAME>Vouchers</REPORTNAME>',
    '   </REQUESTDESC>',
    '   <REQUESTDATA>',
  ];

  vouchers.forEach((v) => {
    const clients = Array.from(v.clients.values());
    const allLines = clients.flatMap((c) => c.invoices);
    const bankAmt = round2(allLines.reduce((s, i) => s + num(i.allocated), 0));
    const tdsAmt = round2(allLines.reduce((s, i) => s + num(i.tds_amount), 0));
    const date = tallyImportDate(v.date);

    out.push('    <TALLYMESSAGE xmlns:UDF="TallyUDF">');
    out.push('     <VOUCHER VCHTYPE="Receipt" ACTION="Create" OBJVIEW="Accounting Voucher View">');
    out.push(`      <DATE>${date}</DATE>`);
    out.push(`      <EFFECTIVEDATE>${date}</EFFECTIVEDATE>`);
    out.push('      <VOUCHERTYPENAME>Receipt</VOUCHERTYPENAME>');
    // Tally numbers the voucher itself. A receipt type set to manual numbering
    // would need a <VOUCHERNUMBER> here instead.
    out.push(`      <NARRATION>${xmlEscape(v.narration || v.trx_id)}</NARRATION>`);
    // named only when the credit belongs to a single client — on a shared credit
    // there is no one party the voucher belongs to
    if (clients.length === 1) {
      out.push(`      <PARTYLEDGERNAME>${xmlEscape(clients[0].name)}</PARTYLEDGERNAME>`);
    }

    // debits first: the money that landed, then anything withheld at source
    ledgerEntry(bankLedgerOf(v), bankAmt, true).forEach((l) => out.push(l));
    if (tdsAmt > 0) ledgerEntry(TDS_LEDGER, tdsAmt, true).forEach((l) => out.push(l));

    // credits: each paying client, with one bill reference per invoice settled
    clients.forEach((c) => {
      const partyAmt = round2(c.invoices.reduce((s, i) => s + num(i.allocated) + num(i.tds_amount), 0));
      // every invoice line of this client, so the allocations always add up to
      // the amount credited — a line with no invoice number goes on account
      const bills = c.invoices.map((i) => ({
        name: (i.reference_no ?? '').toString(),
        amount: round2(num(i.allocated) + num(i.tds_amount)),
      }));
      ledgerEntry(c.name, partyAmt, false, bills).forEach((l) => out.push(l));
    });

    out.push('     </VOUCHER>');
    out.push('    </TALLYMESSAGE>');
  });

  out.push('   </REQUESTDATA>');
  out.push('  </IMPORTDATA>');
  out.push(' </BODY>');
  out.push('</ENVELOPE>');

  return { xml: out.join('\r\n'), vouchers: vouchers.length };
};

/** Write the import file and hand it to the browser. */
export const exportTallyXml = async (lines, filename) => {
  const { xml, vouchers } = buildTallyXml(lines);
  saveAs(new Blob([`<?xml version="1.0" encoding="UTF-8"?>\r\n${xml}`], { type: 'application/xml;charset=utf-8' }), filename);
  return { vouchers };
};
